import { DiagramData } from "@/data/drills";

interface TacticalDiagramProps {
  data: DiagramData;
  className?: string;
}

export const TacticalDiagram = ({ data, className = "" }: TacticalDiagramProps) => {
  const renderPitchLines = () => {
    if (data.type === "full-pitch") {
      return (
        <>
          <line x1="5" y1="50" x2="95" y2="50" stroke="white" strokeWidth="0.4" opacity="0.7" />
          <circle cx="50" cy="50" r="9" fill="none" stroke="white" strokeWidth="0.4" opacity="0.7" />
          <circle cx="50" cy="50" r="0.8" fill="white" opacity="0.7" />
          <rect x="38" y="5" width="24" height="6" fill="none" stroke="white" strokeWidth="0.4" opacity="0.7" />
          <rect x="28" y="5" width="44" height="14" fill="none" stroke="white" strokeWidth="0.4" opacity="0.7" />
          <rect x="38" y="89" width="24" height="6" fill="none" stroke="white" strokeWidth="0.4" opacity="0.7" />
          <rect x="28" y="81" width="44" height="14" fill="none" stroke="white" strokeWidth="0.4" opacity="0.7" />
          <rect x="43" y="2" width="14" height="3" fill="none" stroke="white" strokeWidth="0.6" />
          <rect x="43" y="95" width="14" height="3" fill="none" stroke="white" strokeWidth="0.6" />
        </>
      );
    }

    if (data.type === "full-pitch-no-bottom-box") {
      return (
        <>
          <line x1="5" y1="50" x2="95" y2="50" stroke="white" strokeWidth="0.4" opacity="0.7" />
          <circle cx="50" cy="50" r="9" fill="none" stroke="white" strokeWidth="0.4" opacity="0.7" />
          <circle cx="50" cy="50" r="0.8" fill="white" opacity="0.7" />
          <rect x="38" y="5" width="24" height="6" fill="none" stroke="white" strokeWidth="0.4" opacity="0.7" />
          <rect x="28" y="5" width="44" height="14" fill="none" stroke="white" strokeWidth="0.4" opacity="0.7" />
          <rect x="43" y="2" width="14" height="3" fill="none" stroke="white" strokeWidth="0.6" />
        </>
      );
    }

    if (data.type === "full-pitch-top-only") {
      return (
        <>
          <rect x="38" y="5" width="24" height="6" fill="none" stroke="white" strokeWidth="0.4" opacity="0.7" />
          <rect x="28" y="5" width="44" height="14" fill="none" stroke="white" strokeWidth="0.4" opacity="0.7" />
          <rect x="43" y="2" width="14" height="3" fill="none" stroke="white" strokeWidth="0.6" />
        </>
      );
    }

    if (data.type === "full-pitch-bottom-only") {
      return (
        <>
          <rect x="38" y="89" width="24" height="6" fill="none" stroke="white" strokeWidth="0.4" opacity="0.7" />
          <rect x="28" y="81" width="44" height="14" fill="none" stroke="white" strokeWidth="0.4" opacity="0.7" />
          <rect x="43" y="95" width="14" height="3" fill="none" stroke="white" strokeWidth="0.6" />
        </>
      );
    }

    if (data.type === "full-pitch-empty") {
      return null;
    }

    if (data.type === "full-pitch-wide-goals") {
      return (
        <>
          <line x1="50" y1="5" x2="50" y2="95" stroke="white" strokeWidth="0.4" opacity="0.7" />
          <circle cx="50" cy="50" r="9" fill="none" stroke="white" strokeWidth="0.4" opacity="0.7" />
          <circle cx="50" cy="50" r="0.8" fill="white" opacity="0.7" />
        </>
      );
    }
    
    if (data.type === "half-pitch") {
      return (
        <>
          {/* 6-yard box */}
          <rect x="35" y="5" width="30" height="10" fill="none" stroke="white" strokeWidth="0.4" opacity="0.6" />
          {/* 18-yard box - 65 wide, 28 deep */}
          <rect x="17.5" y="5" width="65" height="28" fill="none" stroke="white" strokeWidth="0.4" opacity="0.6" />
          {/* Penalty arc - smaller */}
          <path d="M 38 33 A 12 12 0 0 0 62 33" fill="none" stroke="white" strokeWidth="0.4" opacity="0.6" />
          {/* Center circle arc at bottom */}
          <path d="M 32 95 A 18 18 0 0 1 68 95" fill="none" stroke="white" strokeWidth="0.4" opacity="0.6" />
          <circle cx="50" cy="95" r="0.8" fill="white" opacity="0.6" />
          {/* Goal */}
          <rect x="40" y="2" width="20" height="3" fill="none" stroke="white" strokeWidth="0.6" />
          {/* Penalty spot */}
          <circle cx="50" cy="22" r="0.8" fill="white" opacity="0.6" />
        </>
      );
    }

    if (data.type === "half-pitch-minimal") {
      return (
        <>
          <rect x="28" y="5" width="44" height="14" fill="none" stroke="white" strokeWidth="0.4" opacity="0.6" />
          <rect x="43" y="2" width="14" height="3" fill="none" stroke="white" strokeWidth="0.6" />
        </>
      );
    }

    // Goal square - zoomed in view of the 18-yard box with 6-yard box and goal
    if (data.type === "goal-square") {
      return (
        <>
          {/* Goal - centered on goal line */}
          <rect x="40" y="7" width="20" height="3" fill="none" stroke="white" strokeWidth="0.6" />
          {/* 6-yard box - small rectangle below goal, attached to goal line */}
          <rect x="30" y="10" width="40" height="18" fill="none" stroke="white" strokeWidth="0.5" opacity="0.7" />
          {/* 18-yard box - bigger rectangle encompassing the 6-yard box, attached to goal line */}
          <rect x="10" y="10" width="80" height="55" fill="none" stroke="white" strokeWidth="0.5" opacity="0.7" />
          {/* Penalty spot */}
          <circle cx="50" cy="45" r="1" fill="white" opacity="0.8" />
          {/* Penalty arc (D) - semi-circle attached to bottom of 18-yard box */}
          <path d="M 35 65 A 15 15 0 0 0 65 65" fill="none" stroke="white" strokeWidth="0.5" opacity="0.7" />
        </>
      );
    }
    
    return null;
  };

  const renderZones = () => {
    return data.zones?.map((zone, i) => (
      <rect
        key={`zone-${i}`}
        x={zone.x}
        y={zone.y}
        width={zone.width}
        height={zone.height}
        fill={zone.color || "rgba(255,255,255,0.02)"}
        stroke="white"
        strokeWidth="0.3"
        strokeDasharray="3,3"
        opacity="0.5"
      />
    ));
  };

  const renderCones = () => {
    return data.cones?.map((cone, i) => (
      <g key={`cone-${i}`}>
        <polygon
          points={`${cone.x},${cone.y - 1.8} ${cone.x - 1.3},${cone.y + 1.3} ${cone.x + 1.3},${cone.y + 1.3}`}
          fill={cone.color || "#FF6B35"}
          stroke="#FF8C5A"
          strokeWidth="0.2"
        />
      </g>
    ));
  };

  const renderGoals = () => {
    return data.goals?.map((goal, i) => (
      <g key={`goal-${i}`} transform={`rotate(${goal.rotation || 0}, ${goal.x}, ${goal.y})`}>
        <rect
          x={goal.x - goal.width / 2}
          y={goal.y - 1}
          width={goal.width}
          height="2"
          fill="none"
          stroke="white"
          strokeWidth="0.8"
        />
        <line
          x1={goal.x - goal.width / 2}
          y1={goal.y}
          x2={goal.x - goal.width / 2}
          y2={goal.y + 2.5}
          stroke="white"
          strokeWidth="0.4"
        />
        <line
          x1={goal.x + goal.width / 2}
          y1={goal.y}
          x2={goal.x + goal.width / 2}
          y2={goal.y + 2.5}
          stroke="white"
          strokeWidth="0.4"
        />
      </g>
    ));
  };

  const renderLines = () => {
    return data.lines?.map((line, i) => {
      const dashArray = line.style === "dotted" ? "2,2" : line.style === "dashed" ? "4,4" : "none";
      return (
        <line
          key={`line-${i}`}
          x1={line.startX}
          y1={line.startY}
          x2={line.endX}
          y2={line.endY}
          stroke={line.color || "white"}
          strokeWidth="0.5"
          strokeDasharray={dashArray}
          opacity="0.8"
        />
      );
    });
  };

  const renderArrows = () => {
    return data.arrows?.map((arrow, i) => {
      const dx = arrow.endX - arrow.startX;
      const dy = arrow.endY - arrow.startY;
      const length = Math.sqrt(dx * dx + dy * dy);
      
      if (length === 0) return null;
      
      const shortenStart = 3;
      const shortenEnd = 3;
      const startX = arrow.startX + (dx / length) * shortenStart;
      const startY = arrow.startY + (dy / length) * shortenStart;
      const endX = arrow.endX - (dx / length) * shortenEnd;
      const endY = arrow.endY - (dy / length) * shortenEnd;

      // Red/maroon for passes to match originals
      const strokeColor = arrow.type === "dribble" ? "#C13C3C" : "#C13C3C";
      const strokeDash = arrow.type === "movement" ? "2,2" : "none";

      // Wavy dribble arrow (sine wave along the line)
      if (arrow.type === "dribble") {
        const segments = Math.max(8, Math.round(length / 2));
        const amplitude = Math.min(2.2, Math.max(1.2, length / 30));
        const waves = Math.max(2, Math.round(length / 6));
        const ux = dx / length;
        const uy = dy / length;
        const nx = -uy;
        const ny = ux;
        let d = `M ${startX} ${startY}`;
        for (let s = 1; s <= segments; s++) {
          const t = s / segments;
          const baseX = startX + (endX - startX) * t;
          const baseY = startY + (endY - startY) * t;
          const offset = Math.sin(t * Math.PI * waves) * amplitude;
          d += ` L ${baseX + nx * offset} ${baseY + ny * offset}`;
        }
        return (
          <g key={`arrow-${i}`}>
            <defs>
              <marker id={`arrowhead-${i}`} markerWidth="4" markerHeight="4" refX="2" refY="2" orient="auto">
                <polygon points="0,0 4,2 0,4" fill={strokeColor} />
              </marker>
            </defs>
            <path
              d={d}
              fill="none"
              stroke={strokeColor}
              strokeWidth="0.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              markerEnd={`url(#arrowhead-${i})`}
            />
          </g>
        );
      }

      if (arrow.curved) {
        const midX = (startX + endX) / 2;
        const perpX = -(endY - startY);
        const perpY = endX - startX;
        const perpLen = Math.sqrt(perpX * perpX + perpY * perpY);
        const curveOffset = 10 * (arrow.curveDirection || 1);
        const ctrlX = midX + (perpX / perpLen) * curveOffset;
        const ctrlY = (startY + endY) / 2 + (perpY / perpLen) * curveOffset;
        
        return (
          <g key={`arrow-${i}`}>
            <defs>
              <marker
                id={`arrowhead-${i}`}
                markerWidth="4"
                markerHeight="4"
                refX="2"
                refY="2"
                orient="auto"
              >
                <polygon points="0,0 4,2 0,4" fill={strokeColor} />
              </marker>
            </defs>
            <path
              d={`M ${startX} ${startY} Q ${ctrlX} ${ctrlY} ${endX} ${endY}`}
              fill="none"
              stroke={strokeColor}
              strokeWidth="0.8"
              strokeDasharray={strokeDash}
              markerEnd={`url(#arrowhead-${i})`}
            />
          </g>
        );
      }

      return (
        <g key={`arrow-${i}`}>
          <defs>
            <marker
              id={`arrowhead-${i}`}
              markerWidth="4"
              markerHeight="4"
              refX="2"
              refY="2"
              orient="auto"
            >
              <polygon points="0,0 4,2 0,4" fill={strokeColor} />
            </marker>
          </defs>
          <line
            x1={startX}
            y1={startY}
            x2={endX}
            y2={endY}
            stroke={strokeColor}
            strokeWidth="0.8"
            strokeDasharray={strokeDash}
            markerEnd={`url(#arrowhead-${i})`}
          />
        </g>
      );
    });
  };

  const renderPlayer = (
    player: { x: number; y: number; label?: string; color?: string },
    type: "attacker" | "defender" | "neutral",
    index: number
  ) => {
    // Colors matching original PDFs:
    // Blue = #3B82F6 (attackers)
    // Red = #C13C3C (defenders)
    // Green/Cyan = #10B981 (neutrals)
    
    const colors = {
      attacker: { fill: "#3B82F6", stroke: "#2563EB", text: "white" },
      defender: { fill: "#C13C3C", stroke: "#991B1B", text: "white" },
      neutral: { fill: "#10B981", stroke: "#059669", text: "white" },
    };

    const { fill, stroke, text } = colors[type];
    const playerColor = player.color || fill;

    // Parse label: could be "number:name", "GK:name", "GK", number, or name
    const hasColon = player.label?.includes(':');
    let insideLabel = player.label;
    let belowLabel = '';
    
    if (hasColon && player.label) {
      const [first, second] = player.label.split(':');
      insideLabel = first; // number or "GK"
      belowLabel = second; // name
    } else if (player.label) {
      const isNumber = /^\d+$/.test(player.label);
      const isGK = player.label === 'GK';
      if (!isNumber && !isGK) {
        // Name only - show below
        insideLabel = '';
        belowLabel = player.label;
      }
    }

    return (
      <g key={`${type}-${index}`}>
        {/* Player circle - filled like originals */}
        <circle
          cx={player.x}
          cy={player.y}
          r="3"
          fill={playerColor}
          stroke={stroke}
          strokeWidth="0.4"
        />
        {/* Player number/GK inside circle */}
        {insideLabel && (
          <text
            x={player.x}
            y={player.y + 1}
            textAnchor="middle"
            fontSize={insideLabel === 'GK' ? '2.5' : '3'}
            fontWeight="700"
            fill={text}
            fontFamily="Arial, sans-serif"
          >
            {insideLabel}
          </text>
        )}
        {/* Name below circle */}
        {belowLabel && (
          <text
            x={player.x}
            y={player.y + 6}
            textAnchor="middle"
            fontSize="2.5"
            fontWeight="700"
            fill="black"
            fontFamily="Arial, sans-serif"
          >
            {belowLabel.toUpperCase()}
          </text>
        )}
      </g>
    );
  };

  const renderBall = () => {
    if (!data.ball) return null;
    return (
      <g>
        <circle
          cx={data.ball.x}
          cy={data.ball.y}
          r="1.5"
          fill="white"
          stroke="#333"
          strokeWidth="0.2"
        />
        <circle
          cx={data.ball.x}
          cy={data.ball.y}
          r="0.6"
          fill="#333"
        />
      </g>
    );
  };

  const renderBalls = () => {
    if (!data.balls) return null;
    return data.balls.map((ball, i) => (
      <g key={`ball-${i}`}>
        <circle
          cx={ball.x}
          cy={ball.y}
          r="1.5"
          fill="white"
          stroke="#333"
          strokeWidth="0.2"
        />
        <circle
          cx={ball.x}
          cy={ball.y}
          r="0.6"
          fill="#333"
        />
      </g>
    ));
  };

  const renderMannequins = () => {
    return data.mannequins?.map((mannequin, i) => (
      <g key={`mannequin-${i}`}>
        {/* Mannequin body - tall rectangle */}
        <rect
          x={mannequin.x - 1}
          y={mannequin.y - 3}
          width="2"
          height="6"
          fill={mannequin.color || "#EF4444"}
          stroke="#DC2626"
          strokeWidth="0.3"
          rx="0.5"
        />
        {/* Mannequin head */}
        <circle
          cx={mannequin.x}
          cy={mannequin.y - 4}
          r="1.2"
          fill={mannequin.color || "#EF4444"}
          stroke="#DC2626"
          strokeWidth="0.3"
        />
      </g>
    ));
  };

  const getViewBox = () => {
    if (data.type === "half-pitch" || data.type === "half-pitch-minimal") {
      return "0 -2 100 102";
    }
    return "0 0 100 100";
  };

  return (
    <div className={`relative overflow-hidden rounded-lg ${className}`}>
      <svg
        viewBox={getViewBox()}
        preserveAspectRatio="xMidYMid meet"
        className="w-full h-full"
        style={{
          background: "linear-gradient(180deg, hsl(120 45% 38%) 0%, hsl(120 45% 32%) 100%)",
        }}
      >
        {/* Pitch stripe pattern */}
        <defs>
          <pattern id="pitch-stripes" width="10" height="100" patternUnits="userSpaceOnUse">
            <rect width="5" height="100" fill="hsl(120 45% 40%)" />
            <rect x="5" width="5" height="100" fill="hsl(120 45% 35%)" />
          </pattern>
        </defs>
        <rect width="100" height="100" fill="url(#pitch-stripes)" />
        
        {/* Pitch border */}
        <rect
          x="5"
          y="5"
          width="90"
          height="90"
          fill="none"
          stroke="white"
          strokeWidth="0.4"
          opacity="0.7"
        />

        {renderZones()}
        {renderPitchLines()}
        {renderCones()}
        {renderGoals()}
        {renderLines()}
        {renderArrows()}
        {renderBall()}
        {renderBalls()}
        
        {/* Render mannequins first (behind players) */}
        {renderMannequins()}
        
        {/* Render players */}
        {data.attackers.map((p, i) => renderPlayer(p, "attacker", i))}
        {data.defenders.map((p, i) => renderPlayer(p, "defender", i))}
        {data.neutrals?.map((p, i) => renderPlayer(p, "neutral", i))}
      </svg>
    </div>
  );
};